// Data service for fetching and processing TSX company data
import Papa from 'papaparse'

class DataService {
  constructor() {
    this.cacheKey = 'tsx_data_cache'
    this.cacheExpiry = 24 * 60 * 60 * 1000 // 24 hours
  }

  // Check if cached data is still valid
  isCacheValid() {
    const cached = localStorage.getItem(this.cacheKey)
    if (!cached) return false
    
    try {
      const { timestamp } = JSON.parse(cached)
      return Date.now() - timestamp < this.cacheExpiry
    } catch {
      return false
    }
  }

  // Get cached data
  getCachedData() {
    try {
      const cached = localStorage.getItem(this.cacheKey)
      if (cached) {
        const { data } = JSON.parse(cached)
        return data
      }
    } catch (error) {
      console.error('Error reading cached data:', error)
    }
    return null
  }

  // Cache data with timestamp
  cacheData(data) {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      }
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheData))
    } catch (error) {
      console.error('Error caching data:', error)
    }
  }

  // Fetch TSX data from multiple sources
  async fetchTSXData() {
    try {
      // Try to get cached data first
      if (this.isCacheValid()) {
        const cachedData = this.getCachedData()
        if (cachedData) {
          console.log('Using cached TSX data')
          return cachedData
        }
      }

      console.log('Fetching fresh TSX data...')
      
      // For now, use static data as primary source
      // Yahoo Finance API has CORS issues in browser
      const staticData = await this.fetchStaticData()
      
      if (staticData.length > 0) {
        console.log(`Fetched ${staticData.length} companies from static data`)
        
        // Process and clean the data
        const processedData = this.processData(staticData)
        
        // Cache the processed data
        this.cacheData(processedData)
        
        console.log(`Total companies loaded: ${processedData.length}`)
        return processedData
      }

      return []
    } catch (error) {
      console.error('Error fetching TSX data:', error)
      return []
    }
  }

  // Remove duplicate companies based on ticker
  removeDuplicates(data) {
    const seen = new Set()
    return data.filter(item => {
      if (seen.has(item.Ticker)) {
        return false
      }
      seen.add(item.Ticker)
      return true
    })
  }

  // Fetch data from Yahoo Finance API
  async fetchFromYahoo() {
    try {
      // Extended list of major TSX companies
      const tsxSymbols = [
        // Banks
        'RY.TO', 'TD.TO', 'BMO.TO', 'BNS.TO', 'CM.TO', 'NA.TO', 'LB.TO',
        // Energy
        'CNQ.TO', 'ENB.TO', 'TRP.TO', 'SU.TO', 'IMO.TO', 'CVE.TO', 'ARX.TO',
        // Transport
        'CNR.TO', 'CP.TO', 'AC.TO', 'CJT.TO',
        // Tech
        'SHOP.TO', 'CSU.TO', 'DSG.TO', 'CLS.TO', 'ENGH.TO',
        // Retail/Consumer
        'ATD.TO', 'L.TO', 'MRU.TO', 'DOL.TO', 'BYD.TO',
        // Insurance
        'MFC.TO', 'GWO.TO', 'IFC.TO', 'IAG.TO', 'SLF.TO',
        // Telecom
        'BCE.TO', 'T.TO', 'RCI-B.TO', 'QBR-B.TO', 'CCA.TO',
        // Utilities
        'FTS.TO', 'CU.TO', 'EMA.TO', 'CPX.TO', 'BLX.TO',
        // Materials
        'ABX.TO', 'AEM.TO', 'FNV.TO', 'WPM.TO', 'AGI.TO',
        // Real Estate
        'CIGI.TO', 'FSV.TO', 'AIF.TO'
      ]

      const promises = tsxSymbols.map(async (symbol) => {
        try {
          const response = await fetch(`https://query1.finance.yahoo.com/v8/finance/chart/${symbol}?interval=1d&range=1y`)
          const data = await response.json()
          
          if (data.chart && data.chart.result && data.chart.result[0]) {
            const result = data.chart.result[0]
            const quote = result.indicators.quote[0]
            const meta = result.meta
            
            // Calculate 52-week high/low from available data
            const validHighs = quote.high.filter(h => h !== null)
            const validLows = quote.low.filter(l => l !== null)
            
            return {
              Ticker: symbol,
              Company: this.getCompanyName(symbol),
              'Price Today': meta.regularMarketPrice,
              'Market Cap': meta.marketCap,
              'P/E': meta.trailingPE,
              'Dividend Yield': meta.trailingAnnualDividendYield ? meta.trailingAnnualDividendYield * 100 : null,
              Sector: this.getSector(symbol),
              Industry: this.getIndustry(symbol),
              'Price 1y ago': quote.open[0],
              '52W High': validHighs.length > 0 ? Math.max(...validHighs) : null,
              '52W Low': validLows.length > 0 ? Math.min(...validLows) : null,
              'ROE': null, // Would need additional API call
              'ROIC': null,
              'P/B': null,
              'EPS': null,
              'Beta': null,
              'Debt to Equity': null
            }
          }
        } catch (error) {
          console.error(`Error fetching ${symbol}:`, error)
        }
        return null
      })

      const results = await Promise.all(promises)
      return results.filter(item => item !== null)
    } catch (error) {
      console.error('Error fetching from Yahoo:', error)
      return []
    }
  }

  // Get company name from ticker
  getCompanyName(ticker) {
    const companyNames = {
      'RY.TO': 'Royal Bank of Canada',
      'TD.TO': 'Toronto-Dominion Bank',
      'BMO.TO': 'Bank of Montreal',
      'BNS.TO': 'Bank of Nova Scotia',
      'CM.TO': 'Canadian Imperial Bank of Commerce',
      'CNQ.TO': 'Canadian Natural Resources',
      'ENB.TO': 'Enbridge Inc.',
      'TRP.TO': 'TC Energy Corporation',
      'CNR.TO': 'Canadian National Railway',
      'CP.TO': 'Canadian Pacific Railway',
      'SHOP.TO': 'Shopify Inc.',
      'CSU.TO': 'Constellation Software Inc.',
      'ATD.TO': 'Alimentation Couche-Tard Inc.',
      'BCE.TO': 'BCE Inc.',
      'T.TO': 'Telus Corporation',
      'FTS.TO': 'Fortis Inc.',
      'ABX.TO': 'Barrick Gold Corporation',
      'AEM.TO': 'Agnico Eagle Mines Limited',
      'FNV.TO': 'Franco-Nevada Corporation',
      'MFC.TO': 'Manulife Financial Corporation',
      'GWO.TO': 'Great-West Lifeco Inc.',
      'IFC.TO': 'Intact Financial Corporation',
      'AC.TO': 'Air Canada',
      'SU.TO': 'Suncor Energy Inc.',
      'IMO.TO': 'Imperial Oil Limited',
      'CVE.TO': 'Cenovus Energy Inc.',
      'ARX.TO': 'ARC Resources Ltd.',
      'CJT.TO': 'Cargojet Inc.',
      'DSG.TO': 'The Descartes Systems Group Inc.',
      'CLS.TO': 'Celestica Inc.',
      'ENGH.TO': 'Enghouse Systems Limited',
      'L.TO': 'Loblaw Companies Limited',
      'MRU.TO': 'Metro Inc.',
      'DOL.TO': 'Dollarama Inc.',
      'BYD.TO': 'Boyd Group Services Inc.',
      'IAG.TO': 'iA Financial Corporation Inc.',
      'SLF.TO': 'Sun Life Financial Inc.',
      'RCI-B.TO': 'Rogers Communications Inc.',
      'QBR-B.TO': 'Quebecor Inc.',
      'CCA.TO': 'Cogeco Communications Inc.',
      'CU.TO': 'Canadian Utilities Limited',
      'EMA.TO': 'Emera Incorporated',
      'CPX.TO': 'Capital Power Corporation',
      'BLX.TO': 'Boralex Inc.',
      'WPM.TO': 'Wheaton Precious Metals Corp.',
      'AGI.TO': 'Alamos Gold Inc.',
      'CIGI.TO': 'Colliers International Group Inc.',
      'FSV.TO': 'FirstService Corporation',
      'AIF.TO': 'Altus Group Limited'
    }
    return companyNames[ticker] || ticker
  }

  // Get sector from ticker
  getSector(ticker) {
    const sectors = {
      'RY.TO': 'Financial Services',
      'TD.TO': 'Financial Services',
      'BMO.TO': 'Financial Services',
      'BNS.TO': 'Financial Services',
      'CM.TO': 'Financial Services',
      'CNQ.TO': 'Energy',
      'ENB.TO': 'Energy',
      'TRP.TO': 'Energy',
      'CNR.TO': 'Industrials',
      'CP.TO': 'Industrials',
      'SHOP.TO': 'Technology',
      'CSU.TO': 'Technology',
      'ATD.TO': 'Consumer Cyclical',
      'BCE.TO': 'Communication Services',
      'T.TO': 'Communication Services',
      'FTS.TO': 'Utilities',
      'ABX.TO': 'Basic Materials',
      'AEM.TO': 'Basic Materials',
      'FNV.TO': 'Basic Materials',
      'MFC.TO': 'Financial Services',
      'GWO.TO': 'Financial Services',
      'IFC.TO': 'Financial Services',
      'AC.TO': 'Industrials',
      'SU.TO': 'Energy',
      'IMO.TO': 'Energy',
      'CVE.TO': 'Energy',
      'ARX.TO': 'Energy',
      'CJT.TO': 'Industrials',
      'DSG.TO': 'Technology',
      'CLS.TO': 'Technology',
      'ENGH.TO': 'Technology',
      'L.TO': 'Consumer Defensive',
      'MRU.TO': 'Consumer Defensive',
      'DOL.TO': 'Consumer Defensive',
      'BYD.TO': 'Consumer Cyclical',
      'IAG.TO': 'Financial Services',
      'SLF.TO': 'Financial Services',
      'RCI-B.TO': 'Communication Services',
      'QBR-B.TO': 'Communication Services',
      'CCA.TO': 'Communication Services',
      'CU.TO': 'Utilities',
      'EMA.TO': 'Utilities',
      'CPX.TO': 'Utilities',
      'BLX.TO': 'Utilities',
      'WPM.TO': 'Basic Materials',
      'AGI.TO': 'Basic Materials',
      'CIGI.TO': 'Real Estate',
      'FSV.TO': 'Real Estate',
      'AIF.TO': 'Real Estate'
    }
    return sectors[ticker] || 'Unknown'
  }

  // Get industry from ticker
  getIndustry(ticker) {
    const industries = {
      'RY.TO': 'Banks - Diversified',
      'TD.TO': 'Banks - Diversified',
      'BMO.TO': 'Banks - Diversified',
      'BNS.TO': 'Banks - Diversified',
      'CM.TO': 'Banks - Diversified',
      'CNQ.TO': 'Oil & Gas E&P',
      'ENB.TO': 'Oil & Gas Midstream',
      'TRP.TO': 'Oil & Gas Midstream',
      'CNR.TO': 'Railroads',
      'CP.TO': 'Railroads',
      'SHOP.TO': 'Software - Application',
      'CSU.TO': 'Software - Application',
      'ATD.TO': 'Specialty Retail',
      'BCE.TO': 'Telecom Services',
      'T.TO': 'Telecom Services',
      'FTS.TO': 'Utilities - Regulated Electric',
      'ABX.TO': 'Gold',
      'AEM.TO': 'Gold',
      'FNV.TO': 'Gold',
      'MFC.TO': 'Insurance - Life',
      'GWO.TO': 'Insurance - Life',
      'IFC.TO': 'Insurance - Property & Casualty',
      'AC.TO': 'Airlines',
      'SU.TO': 'Oil & Gas Integrated',
      'IMO.TO': 'Oil & Gas Integrated',
      'CVE.TO': 'Oil & Gas Integrated',
      'ARX.TO': 'Oil & Gas E&P',
      'CJT.TO': 'Integrated Freight & Logistics',
      'DSG.TO': 'Software - Application',
      'CLS.TO': 'Electronic Components',
      'ENGH.TO': 'Software - Application',
      'L.TO': 'Grocery Stores',
      'MRU.TO': 'Grocery Stores',
      'DOL.TO': 'Discount Stores',
      'BYD.TO': 'Auto & Truck Dealerships',
      'IAG.TO': 'Insurance - Diversified',
      'SLF.TO': 'Insurance - Life',
      'RCI-B.TO': 'Telecom Services',
      'QBR-B.TO': 'Telecom Services',
      'CCA.TO': 'Telecom Services',
      'CU.TO': 'Utilities - Diversified',
      'EMA.TO': 'Utilities - Regulated Electric',
      'CPX.TO': 'Utilities - Independent Power Producers',
      'BLX.TO': 'Utilities - Renewable',
      'WPM.TO': 'Gold',
      'AGI.TO': 'Gold',
      'CIGI.TO': 'Real Estate Services',
      'FSV.TO': 'Real Estate Services',
      'AIF.TO': 'Real Estate Services'
    }
    return industries[ticker] || 'Unknown'
  }

  // Fetch static CSV data as primary source
  async fetchStaticData() {
    try {
      const response = await fetch('/tsx_composite_company_data.csv')
      const csvText = await response.text()
      return this.parseCSV(csvText)
    } catch (error) {
      console.error('Error fetching static data:', error)
      return []
    }
  }

  // Parse CSV data
  parseCSV(csvText) {
    const lines = csvText.split('\n').filter(line => line.trim())
    const headers = lines[0].split(',').map(h => h.trim())
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim())
      const row = {}
      headers.forEach((header, index) => {
        let value = values[index]
        if (value === 'N/A' || value === '' || value === undefined) {
          value = null
        } else if (!isNaN(value) && value !== '') {
          value = parseFloat(value)
        }
        row[header] = value
      })
      return row
    }).filter(row => row.Ticker && row.Company && row.Ticker !== 'N/A' && row.Company !== 'N/A')
  }

  // Process and clean the data
  processData(data) {
    return data.map(item => ({
      ...item,
      'Market Cap': item['Market Cap'] || 0,
      'P/E': item['P/E'] || null,
      'Dividend Yield': item['Dividend Yield'] || null,
      'ROE': item['ROE'] || null,
      'ROIC': item['ROIC'] || null,
      'P/B': item['P/B'] || null,
      'EPS': item['EPS'] || null,
      'Beta': item['Beta'] || null,
      'Debt to Equity': item['Debt to Equity'] || null,
      Sector: item.Sector || 'Unknown',
      Industry: item.Industry || 'Unknown'
    }))
  }

  // Force refresh data (clear cache and fetch new data)
  async refreshData() {
    localStorage.removeItem(this.cacheKey)
    return await this.fetchTSXData()
  }

  // Get data with automatic caching
  async getData() {
    return await this.fetchTSXData()
  }

  // Simulate real-time data updates (for demo purposes)
  async simulateRealTimeData() {
    const baseData = await this.fetchStaticData()
    
    // Add some randomization to simulate real-time updates
    return baseData.map(item => ({
      ...item,
      'Price Today': item['Price Today'] ? item['Price Today'] * (0.95 + Math.random() * 0.1) : null,
      'Market Cap': item['Market Cap'] ? item['Market Cap'] * (0.98 + Math.random() * 0.04) : null
    }))
  }
}

export default new DataService() 